import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ShieldCheckIcon,
  CheckCircleIcon,
  SparklesIcon,
  CogIcon,
  PlayIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon
} from '@heroicons/react/24/outline';
import { connectionsAPI } from '../../api/enhancedApiService';

const OnboardingValidationPage = () => {
  const navigate = useNavigate();
  // `loading` covers the initial getOnboardingData fetch. We default it to
  // true so the spinner shows immediately on mount instead of leaving the
  // page blank while React schedules the effect.
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [runningValidations, setRunningValidations] = useState(false);
  // Run error is separate from loadError. loadError covers the GET
  // /onboarding fetch on page mount; runError covers the POST
  // save-and-run path. We treat them differently in the UI: load
  // errors get the early-mount error card, run errors render inline
  // on the "scan complete" panel so the user can retry without losing
  // their selection state. Catching this separately prevents the
  // 2026-05-11 bug where a failed save-and-run rendered a fake
  // "Scan complete: 1 issue" row built from the error fallback.
  const [runError, setRunError] = useState(null);
  const [onboardingData, setOnboardingData] = useState(null);
  const [validationTemplates, setValidationTemplates] = useState([]);
  const [selectedValidations, setSelectedValidations] = useState(new Set());
  const [validationResults, setValidationResults] = useState(null);
  const [currentStep, setCurrentStep] = useState('generation'); // generation, preview, execution

  useEffect(() => {
    loadOnboardingData();
  }, []);

  const generateFallbackTemplates = (tables) => {
    const templates = [];

    tables.forEach(table => {
      const qualifiedName = table.database && table.schema
        ? `"${table.database}"."${table.schema}"."${table.name}"`
        : `"${table.name}"`;

      // Column-aware checks (only when the discovery payload included columns)
      const idColumns = table.columns?.filter(col =>
        col.name.toLowerCase().includes('id') ||
        col.name.toLowerCase().includes('key')
      ) || [];

      if (idColumns.length > 0) {
        templates.push({
          name: `${table.name} - Primary Key Uniqueness`,
          description: `Ensure ${idColumns[0].name} is unique across all records`,
          table: table.name,
          validation_type: 'uniqueness',
          sql: `SELECT COUNT(*) - COUNT(DISTINCT "${idColumns[0].name}") FROM ${qualifiedName}`,
          expected_result: 0,
          severity: 'high',
          business_impact: 'Data integrity violations can cause application errors'
        });
      }

      const importantColumns = table.columns?.filter(col =>
        col.name.toLowerCase().includes('name') ||
        col.name.toLowerCase().includes('status') ||
        col.name.toLowerCase().includes('date')
      ) || [];

      importantColumns.slice(0, 2).forEach(col => {
        templates.push({
          name: `${table.name} - ${col.name} Completeness`,
          description: `Ensure ${col.name} is never null`,
          table: table.name,
          validation_type: 'completeness',
          sql: `SELECT COUNT(*) FROM ${qualifiedName} WHERE "${col.name}" IS NULL`,
          expected_result: 0,
          severity: 'medium',
          business_impact: 'Missing critical data affects business operations'
        });
      });

      // If no column info was available, generate a baseline non-empty
      // check so the table still gets at least one validation. Many
      // discovery payloads (e.g. BigQuery onboarding) skip per-column
      // details for speed; this keeps the wizard usable without re-doing
      // the discovery round-trip.
      const tableGotColumnChecks = idColumns.length > 0 || importantColumns.length > 0;
      if (!tableGotColumnChecks) {
        templates.push({
          name: `${table.name} - Has Data`,
          description: `Ensure ${table.name} is not empty (row count > 0)`,
          table: table.name,
          validation_type: 'completeness',
          sql: `SELECT COUNT(*) FROM ${qualifiedName}`,
          expected_result: '> 0',
          severity: 'medium',
          business_impact: 'Empty tables typically indicate broken pipelines or stale data'
        });
      }
    });

    return templates;
  };

  const loadOnboardingData = async () => {
    try {
      setLoading(true);
      setLoadError(null);
      const connectionId = localStorage.getItem('onboardingConnectionId');
      const selectedTables = JSON.parse(localStorage.getItem('selectedTables') || '[]');

      if (!connectionId) {
        setLoadError('No connection ID found. Please go back and connect a warehouse first.');
        return;
      }

      // Get enhanced onboarding data with smart analysis
      const data = await connectionsAPI.getOnboardingData(connectionId);
      console.log('Onboarding data received:', data);
      setOnboardingData(data);

      // Tables can live under either `tables` (single-database mode) or
      // `all_tables` (multi-database mode, used by BigQuery + multi-database
      // Snowflake responses). Try both.
      const allTables = data.all_tables || data.tables || [];

      // Filter to only selected tables. If the user didn't select any
      // specifically, fall back to all discovered tables so the validation
      // generator still has something to work with.
      const filteredTables = selectedTables.length > 0
        ? allTables.filter(table => selectedTables.includes(table.name))
        : allTables;

      // Get validation templates from the enhanced data
      const templates = data.recommended_validations || [];
      console.log('Validation templates:', templates);

      // If no templates received, generate fallback templates from table
      // names so the page never lands on an empty state.
      let finalTemplates = templates;
      if (templates.length === 0 && filteredTables.length > 0) {
        console.warn('No validation templates received, generating fallback templates');
        finalTemplates = generateFallbackTemplates(filteredTables);
      }

      setValidationTemplates(finalTemplates);

      // Auto-select high-priority validations
      const autoSelected = new Set(
        finalTemplates
          .filter(v => v.severity === 'high')
          .map((v, index) => index)
      );
      setSelectedValidations(autoSelected);

      // Advance to preview now that data has loaded. The brief delay gives
      // the "generating" animation time to render so the transition doesn't
      // feel jarring.
      setTimeout(() => {
        setCurrentStep('preview');
      }, 1200);

    } catch (error) {
      console.error('Failed to load onboarding data:', error);
      setLoadError(error?.response?.data?.error || error?.message || 'Failed to load onboarding data');
    } finally {
      setLoading(false);
    }
  };

  const handleValidationToggle = (index) => {
    setSelectedValidations(prev => {
      const newSet = new Set(prev);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return newSet;
    });
  };

  const handleSelectAllHighPriority = () => {
    const highPriorityIndices = validationTemplates
      .map((v, index) => ({ validation: v, index }))
      .filter(({ validation }) => validation.severity === 'high')
      .map(({ index }) => index);
    
    setSelectedValidations(new Set(highPriorityIndices));
  };

  const executeValidations = async () => {
    try {
      setRunningValidations(true);
      setRunError(null);
      setCurrentStep('execution');

      const connectionId = localStorage.getItem('onboardingConnectionId');
      if (!connectionId) {
        throw new Error('No connection ID — please reconnect your warehouse before running validations.');
      }

      const selectedTemplates = validationTemplates.filter((_, index) =>
        selectedValidations.has(index)
      );

      // Real call: backend saves these as validation_rules and runs them.
      // Replaces the previous Math.random simulation. The results returned
      // are real pass/fail with actual row counts from the warehouse.
      const response = await connectionsAPI.saveAndRunOnboardingValidations({
        connectionId,
        validations: selectedTemplates,
      });

      const results = (response.results || []).map(r => ({
        ...r,
        executed: true,
      }));

      setValidationResults(results);
      localStorage.setItem('validationResults', JSON.stringify(results));
      console.log(
        `Onboarding validations: ${response.rules_created} created, ` +
        `${response.rules_reused || 0} reused (idempotent retry), ` +
        `${response.rules_failed} failed to save, ` +
        `${results.length} results returned`
      );

    } catch (error) {
      console.error('Failed to execute validations:', error);
      // Surface a real error state, not a fake "scan complete: 1 issue"
      // row. The previous fallback built a synthetic result row which
      // (a) made the Scan-Complete panel count it as a real issue, and
      // (b) didn't get written to localStorage, so the next page read
      // an empty validationResults array and said "no issues
      // discovered" — directly contradicting what the user just saw.
      // Now: render an inline retry banner; leave validationResults
      // null so the next-step navigation is suppressed until the user
      // either retries successfully or explicitly continues.
      setRunError(
        error?.response?.data?.error ||
        error?.message ||
        'Failed to run validations. Please try again.'
      );
      setValidationResults(null);
    } finally {
      setRunningValidations(false);
    }
  };

  const handleContinueToResults = () => {
    navigate('/onboarding/results');
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'high': return 'border-red-200 bg-red-50';
      case 'medium': return 'border-yellow-200 bg-yellow-50';
      default: return 'border-blue-200 bg-blue-50';
    }
  };

  const getSeverityIcon = (severity) => {
    switch (severity) {
      case 'high': return <ExclamationTriangleIcon className="h-5 w-5 text-red-500" />;
      case 'medium': return <InformationCircleIcon className="h-5 w-5 text-yellow-500" />;
      default: return <CheckCircleIcon className="h-5 w-5 text-blue-500" />;
    }
  };

  const getValidationTypeLabel = (type) => {
    const labels = {
      'uniqueness': 'Uniqueness Check',
      'format': 'Format Validation',
      'completeness': 'Completeness Check',
      'range': 'Range Validation'
    };
    return labels[type] || 'Data Quality Check';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <div className="max-w-6xl w-full bg-white rounded-lg shadow-xl p-8">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <ShieldCheckIcon className="h-12 w-12 text-blue-600" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Smart Validation Generation
          </h1>
          <p className="text-lg text-gray-600">
            AI-powered data quality rules tailored to your business data
          </p>
        </div>

        {/* Progress Indicator */}
        <div className="mb-8">
          <div className="flex items-center justify-between text-sm text-gray-500 mb-2">
            <span>Step 5 of 6</span>
            <span>3 minutes remaining</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div className="bg-blue-600 h-2 rounded-full" style={{ width: '83.3%' }}></div>
          </div>
        </div>

        {/* Error State — replaces all other content when loadError is set */}
        {loadError && (
          <div className="py-12">
            <div className="max-w-xl mx-auto bg-red-50 border border-red-200 rounded-lg p-6">
              <div className="flex items-start">
                <ExclamationTriangleIcon className="h-6 w-6 text-red-500 mr-3 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <h3 className="text-lg font-medium text-red-900 mb-2">
                    Couldn&apos;t load validation suggestions
                  </h3>
                  <p className="text-sm text-red-700 mb-4">{loadError}</p>
                  <div className="flex gap-3">
                    <button
                      onClick={loadOnboardingData}
                      className="px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-md hover:bg-red-700"
                    >
                      Retry
                    </button>
                    <button
                      onClick={() => navigate('/onboarding/tables')}
                      className="px-4 py-2 border border-gray-300 text-gray-700 text-sm font-medium rounded-md hover:bg-gray-50"
                    >
                      Back to Tables
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Generation Step — shown during initial data load and brief animation window */}
        {!loadError && currentStep === 'generation' && (
          <div className="text-center py-12">
            <div className="flex justify-center mb-6">
              {/* Real spinner so users can tell something is happening */}
              <div className="relative">
                <SparklesIcon className="h-16 w-16 text-blue-600 animate-pulse" />
                <div className="absolute -bottom-2 left-1/2 -translate-x-1/2">
                  <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-blue-600"></div>
                </div>
              </div>
            </div>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              {loading ? 'Fetching your data structure' : 'Generating Smart Validations'}
            </h2>
            <p className="text-sm text-gray-500 mb-6">
              {loading
                ? 'Reading your tables and columns from the warehouse...'
                : 'Analyzing patterns and building validation rules...'}
            </p>
            <div className="space-y-3 text-gray-600 max-w-md mx-auto">
              <div className="flex items-center justify-center">
                {loading ? (
                  <CogIcon className="h-5 w-5 text-blue-500 mr-2 animate-spin" />
                ) : (
                  <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2" />
                )}
                <span>Analyzing table structures</span>
              </div>
              <div className="flex items-center justify-center">
                {loading ? (
                  <CogIcon className="h-5 w-5 text-gray-400 mr-2 animate-spin" />
                ) : (
                  <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2" />
                )}
                <span>Detecting data patterns</span>
              </div>
              <div className="flex items-center justify-center">
                <CogIcon className="h-5 w-5 text-blue-500 mr-2 animate-spin" />
                <span>Creating validation templates</span>
              </div>
            </div>
          </div>
        )}

        {/* Empty Preview — when load succeeded but no validations could be generated */}
        {!loadError && currentStep === 'preview' && validationTemplates.length === 0 && (
          <div className="py-12">
            <div className="max-w-xl mx-auto bg-blue-50 border border-blue-200 rounded-lg p-6 text-center">
              <InformationCircleIcon className="h-10 w-10 text-blue-500 mx-auto mb-3" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                No automatic validations available for these tables
              </h3>
              <p className="text-sm text-gray-600 mb-4">
                We couldn&apos;t generate validations from the discovered metadata
                alone. You can continue to the dashboard and add custom
                validations from there.
              </p>
              <div className="flex gap-3 justify-center">
                <button
                  onClick={() => navigate('/onboarding/tables')}
                  className="px-4 py-2 border border-gray-300 text-gray-700 text-sm font-medium rounded-md hover:bg-gray-50"
                >
                  Back to Tables
                </button>
                <button
                  onClick={() => navigate('/onboarding/results')}
                  className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700"
                >
                  Continue to Dashboard
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Preview Step */}
        {currentStep === 'preview' && validationTemplates.length > 0 && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="text-2xl font-semibold text-gray-900">
                Recommended Validations
              </h2>
              <div className="flex items-center space-x-4">
                <button
                  onClick={handleSelectAllHighPriority}
                  className="px-4 py-2 bg-red-100 text-red-800 border border-red-300 rounded-md hover:bg-red-200"
                >
                  Select All High Priority
                </button>
                <span className="text-sm text-gray-600">
                  {selectedValidations.size} of {validationTemplates.length} selected
                </span>
              </div>
            </div>

            <div className="grid gap-4">
              {validationTemplates.map((validation, index) => (
                <div
                  key={index}
                  className={`border rounded-lg p-6 cursor-pointer transition-all ${
                    selectedValidations.has(index)
                      ? 'border-blue-400 bg-blue-50'
                      : getSeverityColor(validation.severity)
                  }`}
                  onClick={() => handleValidationToggle(index)}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center mb-3">
                        {getSeverityIcon(validation.severity)}
                        <h3 className="text-lg font-medium text-gray-900 ml-2">
                          {validation.name}
                        </h3>
                        {selectedValidations.has(index) && (
                          <CheckCircleIcon className="h-5 w-5 text-green-500 ml-2" />
                        )}
                      </div>
                      
                      <p className="text-gray-700 mb-3">
                        {validation.description}
                      </p>
                      
                      <div className="flex items-center space-x-4 text-sm">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                          {getValidationTypeLabel(validation.validation_type)}
                        </span>
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          validation.severity === 'high' 
                            ? 'bg-red-100 text-red-800'
                            : validation.severity === 'medium'
                            ? 'bg-yellow-100 text-yellow-800'
                            : 'bg-blue-100 text-blue-800'
                        }`}>
                          {validation.severity} priority
                        </span>
                        <span className="text-gray-500">
                          Table: {validation.table}
                        </span>
                      </div>
                      
                      <div className="mt-3 p-3 bg-gray-50 rounded border">
                        <p className="text-sm text-gray-600 mb-2">
                          <strong>Business Impact:</strong> {validation.business_impact}
                        </p>
                        <details className="text-sm">
                          <summary className="cursor-pointer text-blue-600 hover:text-blue-800">
                            View SQL
                          </summary>
                          <pre className="mt-2 p-2 bg-gray-800 text-green-400 rounded text-xs overflow-x-auto">
                            {validation.sql}
                          </pre>
                        </details>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="flex justify-between">
              <button
                onClick={() => navigate('/onboarding/tables')}
                className="px-6 py-3 border border-gray-300 text-gray-700 font-medium rounded-md shadow-sm hover:bg-gray-50"
              >
                Back to Tables
              </button>
              <button
                onClick={executeValidations}
                disabled={selectedValidations.size === 0}
                className="inline-flex items-center px-6 py-3 bg-blue-600 text-white font-medium rounded-md shadow-sm hover:bg-blue-700 disabled:opacity-50"
              >
                <PlayIcon className="h-5 w-5 mr-2" />
                Run Selected Validations ({selectedValidations.size})
              </button>
            </div>
          </div>
        )}

        {/* Execution Step */}
        {currentStep === 'execution' && (
          <div className="space-y-6">
            <div className="text-center">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                Saving and Running Your Validations
              </h2>
              <p className="text-gray-600">
                {runningValidations
                  ? `Saving ${selectedValidations.size} rules and executing them against your data. This usually takes 30-60 seconds.`
                  : `Tested your data against ${selectedValidations.size} quality rules.`}
              </p>
            </div>

            {runningValidations ? (
              <div className="space-y-4">
                <div className="flex justify-center">
                  <CogIcon className="h-12 w-12 text-blue-600 animate-spin" />
                </div>
                <div className="text-center text-gray-600 space-y-1">
                  <div>Saving validation rules...</div>
                  <div className="text-sm text-gray-500">Querying your warehouse for actual results</div>
                </div>
              </div>
            ) : runError ? (
              <div className="bg-red-50 border border-red-200 rounded-lg p-6">
                <h3 className="text-lg font-medium text-red-900 mb-2 flex items-center">
                  <ExclamationTriangleIcon className="h-5 w-5 mr-2" />
                  Validation run failed
                </h3>
                <p className="text-sm text-red-700 mb-4">{runError}</p>
                <button
                  onClick={executeValidations}
                  className="px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-md hover:bg-red-700"
                >
                  Retry
                </button>
              </div>
            ) : validationResults && (
              <div className="space-y-6">
                <div className="bg-green-50 border border-green-200 rounded-lg p-6">
                  <h3 className="text-lg font-medium text-green-900 mb-2 flex items-center">
                    <CheckCircleIcon className="h-5 w-5 mr-2" />
                    Scan complete
                  </h3>
                  <p className="text-green-700">
                    Found {validationResults.filter(r => !r.passed).length} data quality issues requiring attention.
                  </p>
                </div>

                <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
                  <table className="min-w-full divide-y divide-gray-300">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Table
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Rules
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Passing
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Issues
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {Object.entries(
                        validationResults.reduce((acc, result) => {
                          const table = result.table;
                          if (!acc[table]) {
                            acc[table] = { total: 0, passing: 0, issues: 0 };
                          }
                          acc[table].total += 1;
                          if (result.passed) {
                            acc[table].passing += 1;
                          } else {
                            acc[table].issues += 1;
                          }
                          return acc;
                        }, {})
                      ).map(([tableName, stats]) => (
                        <tr key={tableName}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {tableName}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {stats.total}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {stats.passing}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              stats.issues > 0 ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'
                            }`}>
                              {stats.issues}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="flex justify-end">
                  <button
                    onClick={handleContinueToResults}
                    className="px-6 py-3 bg-blue-600 text-white font-medium rounded-md shadow-sm hover:bg-blue-700"
                  >
                    Continue to Issue Discovery
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default OnboardingValidationPage;